import { z } from 'zod';
import { normalizedUuidSchema as uuidSchema } from './common';

// ---------------------------------------------------------------------------
// designs 도메인 계약 — 디자인 문서(DesignDocument) v1. 모양은 SUPER-41 에서 정했고,
// 저장·조회 API 는 apps/api/src/modules/designs(워프 로드맵 Phase 6, SUPER-71)가 구현한다.
// 그 API 의 입출력 스키마는 design-api.ts, 참조 수집·거부 문구는 이 파일 끝에 있다.
//
// 정책 원본: docs/products/custom-policy.md §3·§4. 구현 계획: docs/design/custom/editor-api-contract-plan.md §5-4.
// 저장 위치는 designs.body(jsonb, = designBodySchema) 이고 productId·colorOptionId 는 designs 컬럼이다.
// 편집영역 버전(editAreaId)은 담지 않는다 — 디자인은 열 때마다 상품의 최신 편집영역 위에 놓인다(정책 §3-1).
// JSON 안에 schemaVersion·coordinateSystem 을 두지 않는다. 형식은 designs.schema_version 컬럼이, 좌표계는 상수가 정한다.
// 필드에 zod 기본값을 두지 않는다 — 저장 JSON 이 자기 완결적이어야 하고 입력·출력 타입이 갈라지지 않게.
// ---------------------------------------------------------------------------

/** designs.schema_version 에 저장하는 현재 형식 라벨. */
export const DESIGN_SCHEMA_VERSION = 1;

/**
 * 디자인 객체 좌표계. 컴포넌트 실측 mm 프레임(원점 좌상단, y 아래)이며 색상·면 전환과 무관하다.
 * 회전은 시계 방향 도 단위, 피벗은 image·text 중심 / group 원점. group 자식 좌표는 group 로컬 원점 기준.
 * z-order 는 layers[]·children[] 의 배열 순서(0 = 최하위)다.
 */
export const DESIGN_COORDINATE_SYSTEM = {
  unit: 'mm',
  origin: 'top-left',
  yAxis: 'down',
  rotation: 'clockwise-degrees',
  pivot: { image: 'center', text: 'center', group: 'origin' },
  zOrder: 'array-index-ascending-bottom-to-top',
} as const;

export const DESIGN_NODE_TYPES = ['image', 'text', 'group'] as const;
export type DesignNodeType = (typeof DESIGN_NODE_TYPES)[number];

export const TEXT_ALIGNS = ['left', 'center', 'right'] as const;
export type TextAlign = (typeof TEXT_ALIGNS)[number];

/** 문서 전체 노드 상한 — designs.body 1MB CHECK 아래에서 여유 있는 값. */
export const MAX_DESIGN_NODES = 200;
export const MAX_GROUP_DEPTH = 5;
export const MAX_TEXT_LENGTH = 1000;
/** 객체 크기 mm 상한 — 편집영역 컴포넌트 상한과 같다. */
export const MAX_DESIGN_MM = 5000;
export const MAX_FONT_SIZE_MM = 500;
export const ROTATION_DEG_RANGE = { min: -180, max: 180 } as const;
export const LETTER_SPACING_EM_RANGE = { min: -0.5, max: 2 } as const;
export const LINE_HEIGHT_RANGE = { min: 0.5, max: 3 } as const;

const sizeMmSchema = z.number().positive().max(MAX_DESIGN_MM);

/** 모든 노드의 공통 프레임. 컴포넌트 밖으로 나간 좌표도 허용한다 — 넘친 부분은 보이지 않을 뿐이다(정책 §3-2). */
const frameShape = {
  /** 문서 전체에서 유일. */
  id: uuidSchema,
  x: z.number(),
  y: z.number(),
  rotationDeg: z.number().min(ROTATION_DEG_RANGE.min).max(ROTATION_DEG_RANGE.max),
};

export const imageNodeSchema = z.strictObject({
  ...frameShape,
  type: z.literal('image'),
  width: sizeMmSchema,
  height: sizeMmSchema,
  /** design_assets.id — READY·소유자 일치·design_asset_refs 동기화는 저장 API(designs 모듈)가 검사한다. */
  assetId: uuidSchema,
  flipX: z.boolean(),
  flipY: z.boolean(),
});
export type ImageNode = z.infer<typeof imageNodeSchema>;

export const textNodeSchema = z.strictObject({
  ...frameShape,
  type: z.literal('text'),
  /** 줄바꿈 폭. */
  width: sizeMmSchema,
  /** 에디터가 레이아웃 뒤 계산한 박스 전체 높이. 서버는 재계산·덮어쓰기하지 않는다(정책 §3-5). */
  height: sizeMmSchema,
  text: z.string().min(1).max(MAX_TEXT_LENGTH),
  /** fonts.id — 존재·product_fonts 연결·미삭제는 저장 API(designs 모듈)가 검사한다. */
  fontId: uuidSchema,
  /** ink_colors.id — 위와 같다(product_ink_colors). */
  inkColorId: uuidSchema,
  fontSizeMm: z.number().positive().max(MAX_FONT_SIZE_MM),
  /** 굵게·기울임은 폰트 파일 없이 합성(faux)한다(정책 §3-4). */
  bold: z.boolean(),
  italic: z.boolean(),
  underline: z.boolean(),
  strikethrough: z.boolean(),
  align: z.enum(TEXT_ALIGNS),
  letterSpacingEm: z.number().min(LETTER_SPACING_EM_RANGE.min).max(LETTER_SPACING_EM_RANGE.max),
  lineHeight: z.number().min(LINE_HEIGHT_RANGE.min).max(LINE_HEIGHT_RANGE.max),
});
export type TextNode = z.infer<typeof textNodeSchema>;

interface NodeFrame {
  id: string;
  x: number;
  y: number;
  rotationDeg: number;
}

/** 그룹은 width·height 가 없다 — 자식 bbox 에서 파생하고 크기 변경은 자식 mm 에 베이크한다. */
export interface GroupNode extends NodeFrame {
  type: 'group';
  /** 순서 = 그룹 안 z-order, 좌표는 그룹 로컬 원점 기준. 1개 이상. */
  children: DesignNode[];
}

export type DesignNode = ImageNode | TextNode | GroupNode;

// 스키마 자체를 최대 깊이까지만 만든다. 파싱 후 깊이를 검사하면 악성 입력이 먼저
// 재귀 파서의 호출 스택을 소진할 수 있다. 마지막 그룹의 자식에는 image·text만 허용한다.
const createNodeSchema = (remainingGroupDepth: number): z.ZodType<DesignNode> => {
  if (remainingGroupDepth === 0) {
    return z.discriminatedUnion('type', [imageNodeSchema, textNodeSchema], {
      error: `그룹 중첩은 ${MAX_GROUP_DEPTH}단계까지입니다.`,
    });
  }

  return z.discriminatedUnion('type', [
    imageNodeSchema,
    textNodeSchema,
    createGroupSchema(remainingGroupDepth),
  ]);
};

const createGroupSchema = (remainingGroupDepth: number) =>
  z.strictObject({
    ...frameShape,
    type: z.literal('group'),
    children: z.array(createNodeSchema(remainingGroupDepth - 1)).min(1),
  });

export const groupNodeSchema = createGroupSchema(MAX_GROUP_DEPTH);
export const designNodeSchema: z.ZodType<DesignNode> = z.discriminatedUnion('type', [
  imageNodeSchema,
  textNodeSchema,
  groupNodeSchema,
]);

export const componentDesignSchema = z.strictObject({
  /** 편집영역 정의의 components[].id — 존재 검사는 저장 API 가 한다. */
  componentId: uuidSchema,
  /** 배열 순서 = z-order(0 = 최하위). 빈 배열 허용. */
  layers: z.array(designNodeSchema),
});
export type ComponentDesign = z.infer<typeof componentDesignSchema>;

interface TreeStats {
  count: number;
}

const walkNodes = (
  nodes: readonly DesignNode[],
  path: readonly (string | number)[],
  seenIds: Set<string>,
  stats: TreeStats,
  ctx: z.RefinementCtx,
): void => {
  nodes.forEach((node, index) => {
    const nodePath = [...path, index];
    stats.count += 1;

    if (seenIds.has(node.id)) {
      ctx.addIssue({
        code: 'custom',
        path: [...nodePath, 'id'],
        message: '노드 id 가 중복됩니다.',
      });
    }
    seenIds.add(node.id);

    if (node.type !== 'group') {
      return;
    }

    walkNodes(node.children, [...nodePath, 'children'], seenIds, stats, ctx);
  });
};

/** 본문(designs.body)과 문서가 공유하는 부분 — 교차 검사·참조 수집은 이것만 본다. */
interface DesignBodyLike {
  readonly components: readonly ComponentDesign[];
}

/** 문서 전체 교차 검사 — componentId 유일, 노드 id 유일, 노드 총합. */
const refineDesignBody = (body: DesignBodyLike, ctx: z.RefinementCtx): void => {
  const componentIds = new Set<string>();
  const nodeIds = new Set<string>();
  const stats: TreeStats = { count: 0 };

  body.components.forEach((component, index) => {
    if (componentIds.has(component.componentId)) {
      ctx.addIssue({
        code: 'custom',
        path: ['components', index, 'componentId'],
        message: '컴포넌트 디자인이 중복됩니다.',
      });
    }
    componentIds.add(component.componentId);

    walkNodes(component.layers, ['components', index, 'layers'], nodeIds, stats, ctx);
  });

  if (stats.count > MAX_DESIGN_NODES) {
    ctx.addIssue({
      code: 'custom',
      path: ['components'],
      message: `디자인 객체는 문서 전체 ${MAX_DESIGN_NODES}개까지입니다.`,
    });
  }
};

const bodyShape = {
  /** componentId 유일. 컴포넌트가 없는 빈 문서도 유효하다(막 만든 디자인). */
  components: z.array(componentDesignSchema),
};

/** designs.body 에 저장되는 본문 — productId·colorOptionId 를 뺀 것. */
export const designBodySchema = z.strictObject(bodyShape).superRefine(refineDesignBody);
export type DesignBody = z.infer<typeof designBodySchema>;

/** 문서 키 세 개 — 응답 스키마(design-api.ts 의 designSchema)가 펼쳐 쓴다. 교차 검사는 designDocumentSchema 에만 있다. */
export const designDocumentShape = {
  productId: uuidSchema,
  /** product_color_options.id — 최신 정의 colors[] 포함·공급 가능 여부는 저장 API 가 검사한다. */
  colorOptionId: uuidSchema,
  ...bodyShape,
};

/**
 * 디자인 문서 — 클라이언트와 서버가 주고받는 단위. productId·colorOptionId 는 designs 컬럼으로,
 * 나머지는 designs.body 로 간다. editAreaId 는 없다(strict 라 넣으면 거부).
 */
export const designDocumentSchema = z
  .strictObject(designDocumentShape)
  .superRefine(refineDesignBody);
export type DesignDocument = z.infer<typeof designDocumentSchema>;

// ---------------------------------------------------------------------------
// 참조 수집 — 저장 API 의 검증(색상·컴포넌트·폰트·잉크·에셋)과 design_asset_refs 동기화가 공유한다.
// 파싱(uuid 소문자 정규화)을 거친 문서를 받는다는 전제라 여기서 다시 정규화하지 않는다.
// ---------------------------------------------------------------------------

/** 문서가 참조하는 외부 id 들 — 그룹 안 노드까지 포함하고 중복은 제거된다. 잠금 순서(오름차순)는 호출자가 정렬한다. */
export interface DesignRefs {
  /** 편집영역 정의 components[].id */
  readonly componentIds: ReadonlySet<string>;
  /** design_assets.id (image 노드) */
  readonly assetIds: ReadonlySet<string>;
  /** fonts.id (text 노드) */
  readonly fontIds: ReadonlySet<string>;
  /** ink_colors.id (text 노드) */
  readonly inkColorIds: ReadonlySet<string>;
}

/** 그룹을 평탄화해 image·text 노드만 돌려준다 — 깊이는 파서가 이미 MAX_GROUP_DEPTH 로 막았다. */
const flattenLeafNodes = (nodes: readonly DesignNode[]): readonly (ImageNode | TextNode)[] =>
  nodes.flatMap((node) => (node.type === 'group' ? flattenLeafNodes(node.children) : [node]));

export const collectDesignRefs = (source: DesignBodyLike): DesignRefs => {
  const leaves = source.components.flatMap((component) => flattenLeafNodes(component.layers));
  const images = leaves.filter((node): node is ImageNode => node.type === 'image');
  const texts = leaves.filter((node): node is TextNode => node.type === 'text');

  return {
    componentIds: new Set(source.components.map((component) => component.componentId)),
    assetIds: new Set(images.map((node) => node.assetId)),
    fontIds: new Set(texts.map((node) => node.fontId)),
    inkColorIds: new Set(texts.map((node) => node.inkColorId)),
  };
};

// ---------------------------------------------------------------------------
// 저장·조회 API 거부 문구(결정 D8·E1). web 은 message 를 이 상수와 대조해 안내를 가른다 — 코드(404/403/400)만으로는
// '없음' 과 '단종' 을 구분할 수 없기 때문이다. 상품 상태·색상 문구는 docs/products/custom-policy.md §4 원문.
// 편집영역 없음(404) 문구는 editor.ts 의 EDIT_AREA_NOT_FOUND_MESSAGE, 이미지 업로드 문구는 design-asset.ts.
// ---------------------------------------------------------------------------

/** 404 — 타인 소유·없음을 같은 문구로 숨긴다. */
export const DESIGN_NOT_FOUND_MESSAGE = '디자인을 찾을 수 없습니다.';
/** 404 — create 시 상품이 미공개·삭제·없음(products.get 과 같은 존재 숨김). */
export const DESIGN_PRODUCT_NOT_FOUND_MESSAGE = '상품을 찾을 수 없습니다.';
/** 403 — 기존 디자인의 상품이 삭제됨. 데이터는 보존된다. */
export const DESIGN_PRODUCT_DISCONTINUED_MESSAGE = '단종된 제품입니다.';
/** 403 — 기존 디자인의 상품이 HIDDEN(비PUBLISHED). 다시 공개되면 풀린다. */
export const DESIGN_PRODUCT_HIDDEN_MESSAGE = '판매가 잠시 중단된 제품입니다.';
/** 400 — colorOptionId 가 최신 편집영역 정의 colors[] 에 없다. */
export const DESIGN_COLOR_UNAVAILABLE_MESSAGE = '선택했던 색상이 더 이상 제공되지 않습니다.';
/** 400 — colorOptionId 는 정의에 있으나 product_color_options.is_available = false. */
export const DESIGN_COLOR_SUPPLY_UNAVAILABLE_MESSAGE =
  '선택한 제품 색상은 현재 공급이 불가합니다. 다른 색상을 선택해 주세요.';
/** 400 — update 입력의 document.productId 가 designs.product_id 와 다르다(디자인은 상품을 바꿀 수 없다). */
export const DESIGN_PRODUCT_IMMUTABLE_MESSAGE = '디자인의 상품은 바꿀 수 없습니다.';
/** 409 — 잠금 아래에서도 refs 유니크(ux_design_asset_refs_live)가 최후 방어로 걸린 저장 경합. */
export const DESIGN_SAVE_CONFLICT_MESSAGE = '디자인 저장이 경합했습니다. 다시 시도해 주세요.';
/** 400 — 사전 검사를 지나 DB CHECK·트리거(23514)에 걸린 저장. 어느 제약인지는 가르지 않는다. */
export const DESIGN_SAVE_REJECTED_MESSAGE =
  '저장할 수 없는 디자인입니다. 이미지와 이름을 확인한 뒤 다시 시도해 주세요.';

// 아래 넷은 목록형 400 — designRejectionWithIds 가 ': id, id' 를 붙인다. web 은 startsWith 로 대조한다.
/** componentId 가 최신 편집영역 정의 components[] 에 없다. */
export const DESIGN_COMPONENT_UNKNOWN_MESSAGE = '편집영역에 없는 컴포넌트입니다';
/** fontId 가 삭제됐거나 product_fonts 에 연결돼 있지 않다. */
export const DESIGN_FONT_NOT_ALLOWED_MESSAGE = '이 상품에서 사용할 수 없는 폰트가 있습니다';
/** inkColorId 가 삭제됐거나 product_ink_colors 에 연결돼 있지 않다. */
export const DESIGN_INK_COLOR_NOT_ALLOWED_MESSAGE =
  '이 상품에서 사용할 수 없는 잉크 색상이 있습니다';
/** assetId 가 READY 가 아니거나 타인 소유·없음이다(세 경우를 같은 문구로 숨긴다). */
export const DESIGN_ASSET_UNAVAILABLE_MESSAGE = '사용할 수 없는 이미지가 있습니다';

/** 목록형 400 문구 조립 — `${message}: id1, id2`. */
export const designRejectionWithIds = (message: string, ids: readonly string[]): string =>
  `${message}: ${ids.join(', ')}`;
